import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'

// Components
import {
    Grid,
    GridItem,
    Text,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {LinkBox, LinkOverlay, UnorderedList, ListItem, Image} from '@chakra-ui/react'
import {categoryUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import useMultiSite from '../../hooks/use-multi-site'

/**
 * Navigation menu level 2 Page Designer category component
 *
 * @param {object} props - props object
 * @param {string} props.categoryData - the category id to link to
 * @param {boolean} props.isOpen - is the drawer open
 * @returns {React.ReactElement}
 */
const DrawerMenuLvl2PDCustomCategory = ({categoryData, isOpen}) => {
    const {buildUrl} = useMultiSite()
    const url = buildUrl(categoryUrlBuilder({id: categoryData?.category}))
    const styles = useStyleConfig('Link')

    return (
        <LinkBox as="article" textAlign="center" height={'100%'} pb={['10px', null, '14px']}>
            <Image
                src={categoryData?.image?.url}
                alt={categoryData?.title}
                ignoreFallback
                height={'auto'}
                width={'100%'}
            />
            <LinkOverlay
                as={RouteLink}
                to={url}
                tabIndex={isOpen ? 0 : -1}
                mt={['10px', null, '8px']}
                display={'block'}
                {...styles}
                textDecoration={'none'}
            >
                <Text
                    as="span"
                    fontFamily="heading"
                    textTransform={'uppercase'}
                    fontSize="12px"
                    lineHeight={1}
                    display={'block'}
                    color={categoryData?.titleColor || 'black'}
                >
                    {categoryData?.title}
                </Text>
            </LinkOverlay>
            {categoryData?.subtitle && (
                <Text
                    variant="bodySmall"
                    color={categoryData?.subtitleColor || 'darkGray'}
                    mt="2px"
                >
                    {categoryData?.subtitle}
                </Text>
            )}
        </LinkBox>
    )
}

/**
 * Navigation menu level 2 Page Designer categories component
 *
 * @param {object} props - props object
 * @param {object} props.item - the item to display
 * @param {boolean} props.isOpen - is the drawer open
 * @returns {React.ReactElement}
 */
export const DrawerMenuLvl2PDCustomCategories = ({item, isOpen, ...props}) => {
    const categoriesData =
        item.regions?.filter((region) => region.id === 'pdc_custom_category_links')[0]
            ?.components || []

    return (
        <Grid as={UnorderedList} templateColumns={'repeat(2, 1fr)'} gap="10px" m={0} {...props}>
            {categoriesData.map((categoryData, i) => {
                return (
                    <GridItem
                        as={ListItem}
                        listStyleType="none"
                        key={categoryData?.id}
                        colSpan={i % 3 === 2 ? 2 : 1}
                    >
                        <DrawerMenuLvl2PDCustomCategory
                            categoryData={categoryData?.data}
                            isOpen={isOpen}
                        />
                    </GridItem>
                )
            })}
        </Grid>
    )
}

DrawerMenuLvl2PDCustomCategories.propTypes = {
    item: PropTypes.object.isRequired,
    isOpen: PropTypes.bool
}

DrawerMenuLvl2PDCustomCategory.propTypes = {
    categoryData: PropTypes.string.isRequired,
    isOpen: PropTypes.bool
}

export default DrawerMenuLvl2PDCustomCategories
